import { logDataModels } from '../../dataModelsLogger';
import type { GovernancePolicyRecord } from '../../components/governanceStore';
import { POLICY_USERS } from '../catalog';
import { currentPolicyExpression } from '../queryTest';
import { summarizeAppliesTo } from '../engine';
import type { GovernancePolicy, PolicyAsset, PolicyModel, PolicyVault, SubjectSelector } from '../types';
import type {
  GraphActionKind,
  GraphAsset,
  GraphCatalog,
  GraphClassification,
  GraphMaskKind,
  GraphPolicy,
  GraphPolicyAction,
  GraphSubject,
  GraphUserContext,
  OwnershipLevel,
} from './policyGraphTypes';

function graphUsers(): GraphUserContext[] {
  return POLICY_USERS.map((user) => ({
    id: user.id,
    label: `${user.name} (${user.role})`,
    roles: [...user.roles],
    region: user.region,
  }));
}

function audienceFromSubject(subject?: SubjectSelector): GraphSubject {
  if (!subject || subject.mode === 'everyone') return { mode: 'everyone', label: 'Everyone' };
  if (subject.mode === 'roles' && subject.roles.length) {
    return { mode: 'roles', roles: [...subject.roles], label: subject.roles.join(', ') };
  }
  if (subject.mode === 'users' && subject.userIds.length) {
    return { mode: 'everyone', label: `${subject.userIds.length} users` };
  }
  if (subject.mode === 'groups' && subject.groups.length) {
    return { mode: 'everyone', label: subject.groups.join(', ') };
  }
  if (subject.mode === 'attributes') {
    return { mode: 'everyone', label: `${subject.attribute.key} ${subject.attribute.operator} ${subject.attribute.value}`.trim() };
  }
  if (subject.mode === 'expression' && subject.expression) {
    return { mode: 'everyone', label: subject.expression };
  }
  return { mode: 'everyone', label: 'Everyone' };
}

function actionFromPolicy(policy: GovernancePolicy): GraphPolicyAction {
  const first = policy.rules[0]?.action;
  if (policy.type === 'ROW_LEVEL' || policy.type === 'TIME_BASED') {
    const filter = currentPolicyExpression(policy).trim();
    return { kind: 'filter', filter: filter || undefined, label: policy.type === 'TIME_BASED' ? 'Time Window' : 'Row Filter' };
  }
  if (policy.type === 'ACCESS_CONTROL') {
    const deny = first?.type === 'ACCESS' && first.effect === 'deny';
    return deny
      ? { kind: 'deny', denyTarget: 'asset', label: 'Deny Access' }
      : { kind: 'allow', label: 'Allow Access' };
  }
  const maskKind = first && (first.type === 'COLUMN' || first.type === 'SENSITIVE') ? first.mask.kind : 'none';
  if (maskKind === 'deny_column') return { kind: 'deny', denyTarget: 'column', label: 'Deny Access' };
  if (maskKind === 'deny_asset') return { kind: 'deny', denyTarget: 'asset', label: 'Deny Access' };
  const mask: GraphMaskKind = maskKind.startsWith('partial') ? 'partial' : 'full';
  const kind: GraphActionKind = 'mask';
  return { kind, mask, label: mask === 'partial' ? 'Partial Mask' : 'Full Mask' };
}

function toGraphPolicy(record: GovernancePolicyRecord, assets: PolicyAsset[]): GraphPolicy | undefined {
  if (record.library || !record.detail) return undefined;
  const policy = record.detail;
  const ownershipLevel: OwnershipLevel = policy.scope.level;
  return {
    id: policy.id || record.id,
    name: policy.name || record.name,
    description: policy.description || record.description,
    type: policy.type,
    status: policy.status === 'ACTIVE' ? 'ACTIVE' : 'DRAFT',
    ownership: {
      level: ownershipLevel,
      vaultId: policy.scope.vaultId,
      modelId: policy.scope.modelId,
      assetId: policy.scope.assetId,
    },
    mandatory: record.flag === 'mandatory',
    audience: policy.rules.length > 1
      ? { mode: 'everyone', label: policy.rules.map((rule) => audienceFromSubject(rule.subject).label).join(' · ') }
      : audienceFromSubject(policy.rules[0]?.subject),
    targeting: record.appliesTo || summarizeAppliesTo(policy, assets),
    action: actionFromPolicy(policy),
    match: {
      assetIds: policy.target.mode === 'selected' || policy.target.mode === 'current' ? [...policy.target.assetIds] : undefined,
      columnNames: policy.target.columnRefs.length ? policy.target.columnRefs.map((ref) => ref.column) : undefined,
      classifications: policy.target.match.classifications.length
        ? (policy.target.match.classifications as GraphClassification[])
        : undefined,
    },
  };
}

function toGraphAsset(asset: PolicyAsset, vaultIds: Set<string>): GraphAsset | undefined {
  if (!vaultIds.has(asset.vaultId)) return undefined;
  return {
    id: asset.id,
    name: asset.name,
    vaultId: asset.vaultId,
    modelIds: [...asset.modelIds],
    source: asset.connectionType || 'Source',
    featured: Boolean(asset.sourceId),
    columns: asset.columns.map((column) => ({
      name: column.name,
      type: column.type,
      classification: (column.classification ?? 'Internal') as GraphClassification,
    })),
  };
}

export function buildGraphCatalog(input: {
  vaults: PolicyVault[];
  models: PolicyModel[];
  assets: PolicyAsset[];
  policies: GovernancePolicyRecord[];
}): GraphCatalog {
  const vaultIds = new Set(input.vaults.map((vault) => vault.id));
  const assets = input.assets.map((asset) => toGraphAsset(asset, vaultIds)).filter((item): item is GraphAsset => Boolean(item));
  const models = input.models
    .filter((model) => vaultIds.has(model.vaultId))
    .map((model) => ({
      id: model.id,
      name: model.name,
      vaultId: model.vaultId,
      assetIds: assets.filter((asset) => asset.modelIds.includes(model.id)).map((asset) => asset.id),
    }));
  const policies = input.policies
    .map((record) => toGraphPolicy(record, input.assets))
    .filter((item): item is GraphPolicy => item !== undefined && vaultIds.has(item.ownership.vaultId));
  logDataModels('app', 'policy_graph_catalog_built', {
    vaults: input.vaults.length,
    models: models.length,
    assets: assets.length,
    policies: policies.length,
  });
  return {
    vaults: input.vaults.map((vault) => ({
      id: vault.id,
      name: vault.name,
      description: vault.connections.length ? vault.connections.join(', ') : 'WorkSpace',
    })),
    models,
    assets,
    policies,
    users: graphUsers(),
  };
}
